import { cva, VariantProps } from "cva";
import { AnimatePresence, motion } from "framer-motion";
import { Check, LucideIcon, Minus } from "lucide-react";
import {
  composeRenderProps,
  Checkbox as RACCheckbox,
  type CheckboxProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "./utils";

type Props = {
  boxClassName?: string;
  checkClassName?: string;
  checkedIcon?: LucideIcon;
  iconColor?: string;
} & CheckboxProps;

const MotionMinus = motion.create(Minus, { forwardMotionProps: true });
const MotionCheck = motion.create(Check, { forwardMotionProps: true });

const checkboxStyles = cva({
  base: "flex gap-2 items-center group text-sm transition relative",
  variants: {
    isDisabled: {
      false: "opacity-100 cursor-pointer",
      true: "opacity-50 cursor-not-allowed",
    },
  },
});

const boxStyles = cva({
  base: "shrink-0 rounded-sm flex items-center justify-center border transition border-theme-outline",
  variants: {
    size: {
      xs: "size-5",
      sm: "size-6",
      md: "size-8",
    },
    isInvalid: {
      true: "[--color:var(--color-red-700)] dark:[--color:var(--color-red-600)] forced-colors:[--color:Mark]! group-pressed:[--color:var(--color-red-800)] dark:group-pressed:[--color:var(--color-red-700)]",
    },
    isDisabled: {
      true: "cursor-not-allowed disabled:opacity-50",
      false: "cursor-pointer",
    },
  },
  defaultVariants: { size: "sm", isDisabled: false },
});

const iconStyles = cva({
  base: "text-green-600 dark:text-green-400",
  variants: {
    size: { xs: "size-3", sm: "size-4", md: "size-6" },
  },
  defaultVariants: { size: "sm" },
});

export function Checkbox({
  checkClassName,
  boxClassName,
  checkedIcon,
  iconColor,
  size = "sm",
  ...props
}: Props & VariantProps<typeof boxStyles & typeof iconStyles>) {
  return (
    <RACCheckbox
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        twMerge(
          focusRing(renderProps),
          checkboxStyles({ ...renderProps, className }),
        ),
      )}
    >
      {({ isSelected, isIndeterminate, isDisabled, ...renderProps }) => (
        <>
          <div
            className={twMerge(
              boxStyles({
                isDisabled: isDisabled,
                size,
                ...renderProps,
              }),
              boxClassName,
            )}
          >
            <AnimatePresence initial={true} mode="wait">
              {isIndeterminate ? (
                <MotionMinus
                  color={iconColor}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  aria-hidden
                  className={twMerge(iconStyles({ size }), checkClassName)}
                />
              ) : isSelected ? (
                <>
                  {checkedIcon ?? (
                    <MotionCheck
                      color={iconColor}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      aria-hidden
                      className={twMerge(iconStyles({ size }), checkClassName)}
                    />
                  )}
                </>
              ) : null}
            </AnimatePresence>
          </div>
          {props.children}
        </>
      )}
    </RACCheckbox>
  );
}

// const AnimatedCheckIcon = (props: ComponentPropsWithRef<typeof motion.svg>) => (
//   <motion.svg
//     width="24"
//     height="24"
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//     initial={{ scale: 0.8 }}
//     animate={{ scale: 1 }}
//     exit={{ scale: 0.8 }}
//     transition={{ duration: 0.15 }}
//     {...props}
//   >
//     <motion.path
//       d="M20 6 9 17l-5-5"
//       initial={{ pathLength: 0 }}
//       animate={{ pathLength: 2 }}
//       exit={{ pathLength: 0 }}
//       transition={{ duration: 0.25, delay: 0.1 }}
//     />
//   </motion.svg>
// );
